
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface LupsPaymentRequestSummary {
  id: string;
  lupsRequestId: string | null;
  sendStatus: string;
  mechanism: string | null;
  collectedAt: string | null;
  abandonedAt: string | null;
  disabledAt: string | null;
  disableReason: string | null;
  sendAttempts: number;
  nextRetryAt: string | null;
  lastError: string | null;
  resolutionNote: string | null;
}

interface Installment {
  installmentDetailId: string; commissionRate: string; commissionAmount: string;
  advanceDays: number; installmentAmount: string | null; paymentAmount: string | null;
  dueAt: string | null; paymentStatus: string | null;
  collectionStatus: string; mechanism: string | null;
  lupsPaymentRequest: LupsPaymentRequestSummary | null;
}

interface AdvanceRequestDetail {
  id: string; companyId: string; companyTaxId: string | null; companyBusinessName: string | null;
  status: string; totalAmount: string | null; discountAmount: string | null; stampTaxAmount: string | null;
  stampTaxRate: string | null; stampTaxCap: string | null; netAmount: string | null; monthlyRate: string | null;
  rejectionReason: string | null; resolutionAt: string | null; cancelableUntil: string | null;
  createdAt: string; installments: Installment[];
}

function formatMoney(val: string | null) {
  if (!val) return '—';
  return `$${Number(val).toLocaleString('es-CL', { minimumFractionDigits: 0 })}`;
}

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  requested: { label: 'Pendiente', color: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800', icon: 'schedule' },
  approved: { label: 'Aprobada', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800', icon: 'check_circle' },
  disbursed: { label: 'Desembolsada', color: 'text-sky-700 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-400 border-sky-200 dark:border-sky-800', icon: 'payments' },
  in_collection: { label: 'En cobro', color: 'text-violet-700 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200 dark:border-violet-800', icon: 'sync' },
  collected: { label: 'Cobrada', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800', icon: 'task_alt' },
  uncollectible: { label: 'Incobrable', color: 'text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800', icon: 'dangerous' },
  cancelled: { label: 'Cancelada', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700', icon: 'cancel' },
};

const INSTALLMENT_COLLECTION_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400' },
  sent: { label: 'Enviada Lups', color: 'text-sky-700 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-400' },
  collected: { label: 'Cobrada', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400' },
  uncollectible: { label: 'Incobrable', color: 'text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400' },
};

// El backend solo emite `mechanism = 'PAC' | null` (enum PaymentMechanism). Los
// reintentos de Lups son internos al lado Lups y no se modelan en FLUX, así que
// no se etiquetan acá.
const MECHANISM_LABEL: Record<string, string> = {
  PAC: 'PAC',
};

const LUPS_SEND_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente envío', color: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400' },
  sent: { label: 'Enviada Lups', color: 'text-sky-700 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-400' },
  in_collection: { label: 'En cobro', color: 'text-violet-700 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400' },
  resolved: { label: 'Resuelta', color: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400' },
  send_failed: { label: 'Falló envío', color: 'text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400' },
  disabled: { label: 'Deshabilitada', color: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400' },
  pac_native: { label: 'PAC nativo', color: 'text-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400' },
};

const DISABLE_REASON_LABEL: Record<string, string> = {
  admin_external_pac_collected: 'Cobro PAC',
  admin_manual: 'Deshabilitada manual',
};

function MetricCard({ label, value, variant }: { label: string; value: string; variant?: 'default' | 'negative' | 'positive' }) {
  const color = variant === 'negative' ? 'text-red-600 dark:text-red-400' : variant === 'positive' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-900 dark:text-white';
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-lg font-bold font-mono ${color}`}>{value}</p>
    </div>
  );
}

export default function AdvanceRequestDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get('id');

  const [request, setRequest] = useState<AdvanceRequestDetail | null>(null);
  const [mel, setMel] = useState<{ liquidMel: number; outstanding: number; available: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectChallenge, setRejectChallenge] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveChallenge, setApproveChallenge] = useState('');

  useEffect(() => { if (requestId) loadRequest(); }, [requestId]);

  async function loadRequest() {
    if (!requestId) return;
    try {
      const url = getAdminEndpointUrl('advanceRequests', 'detail', { id: requestId });
      const detail = await adminHttpClient.get<AdvanceRequestDetail>(url);
      setRequest(detail);
      // Cargar MEL del comercio en paralelo a la solicitud (best-effort: si falla, panel sigue funcionando).
      try {
        const melUrl = getAdminEndpointUrl('mel', 'available', { companyId: detail.companyId });
        setMel(await adminHttpClient.get<{ liquidMel: number; outstanding: number; available: number }>(melUrl));
      } catch (e) {
        console.warn('No se pudo cargar MEL del comercio', e);
        setMel(null);
      }
    } catch (err) { console.error('Error cargando solicitud:', err); }
    finally { setLoading(false); }
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  }

  const expectedName = (request?.companyBusinessName || '').trim().toLowerCase();
  const approveMatches = !!expectedName && approveChallenge.trim().toLowerCase() === expectedName;
  const rejectMatches = !!expectedName && rejectChallenge.trim().toLowerCase() === expectedName;
  const requestedAmount = request ? Number(request.totalAmount ?? '0') : 0;
  // R28: si el MEL disponible no cubre el monto solicitado, no permitimos aprobar desde la UI
  // (el backend igual rechazaría con HTTP 422 MEL_INSUFFICIENT, pero damos feedback inmediato).
  const excedeMel = mel !== null && requestedAmount > mel.available;
  const melDelta = mel !== null ? mel.available - requestedAmount : 0;

  async function handleApprove() {
    if (!requestId || !approveMatches) return;
    setActionLoading('approve');
    try {
      await adminHttpClient.post(getAdminEndpointUrl('advanceRequests', 'approve', { id: requestId }));
      showMessage('success', 'Solicitud aprobada exitosamente');
      setApproveDialogOpen(false); setApproveChallenge('');
      await loadRequest();
    } catch (err: any) { showMessage('error', err.message); }
    finally { setActionLoading(''); }
  }

  async function handleReject() {
    if (!requestId || !rejectionReason.trim() || !rejectMatches) return;
    setActionLoading('reject');
    try {
      await adminHttpClient.post(getAdminEndpointUrl('advanceRequests', 'reject', { id: requestId }), { rejectionReason: rejectionReason.trim() });
      showMessage('success', 'Solicitud rechazada');
      setRejectDialogOpen(false); setRejectionReason(''); setRejectChallenge('');
      await loadRequest();
    } catch (err: any) { showMessage('error', err.message); }
    finally { setActionLoading(''); }
  }

  if (!requestId) return <AdminLayout title="Detalle Solicitud"><div className="text-center py-16 text-slate-500">ID no especificado</div></AdminLayout>;
  if (loading) return <AdminLayout title="Cargando..."><div className="flex justify-center py-20"><span className="material-symbols-outlined text-3xl text-blue-500 animate-spin">progress_activity</span></div></AdminLayout>;
  if (!request) return <AdminLayout title="Detalle Solicitud"><div className="text-center py-16 text-slate-500">Solicitud no encontrada</div></AdminLayout>;

  const sc = statusConfig[request.status] || statusConfig.requested;

  return (
    <AdminLayout
      title="Solicitud de Adelanto"
      subtitle={`${request.companyBusinessName || '—'} — ${request.companyTaxId || '—'}`}
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/solicitudes')} className="gap-1.5">
          <span className="material-symbols-outlined text-[16px]">arrow_back</span> Volver
        </Button>
      }
    >
      <div className="space-y-6 max-w-5xl">
        {message && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            <span className="material-symbols-outlined text-[18px]">{message.type === 'success' ? 'check_circle' : 'error'}</span>
            {message.text}
          </div>
        )}

        {/* Status bar + actions */}
        <div className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg border ${sc.color}`}>
              <span className="material-symbols-outlined text-[18px]">{sc.icon}</span>
              {sc.label}
            </div>
            <span className="text-xs text-slate-400 font-mono">ID: {request.id}</span>
          </div>
          {request.status === 'requested' && (
            <div className="flex gap-2">
              <Button
                size="sm" className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm"
                disabled={actionLoading === 'approve' || excedeMel}
                title={excedeMel ? 'No se puede aprobar: el monto solicitado excede el MEL disponible' : undefined}
                onClick={() => setApproveDialogOpen(true)}
              >
                <span className="material-symbols-outlined text-[16px]">check</span>
                {actionLoading === 'approve' ? 'Procesando...' : 'Aprobar'}
              </Button>
              <Button
                size="sm" variant="destructive" className="h-9 gap-1.5 shadow-sm"
                disabled={actionLoading === 'reject'} onClick={() => setRejectDialogOpen(true)}
              >
                <span className="material-symbols-outlined text-[16px]">close</span>
                Rechazar
              </Button>
            </div>
          )}
        </div>

        {/* MEL del comercio + sobrecupo (F2 tarea 2.5) */}
        {request.status === 'requested' && mel && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-slate-500 text-[18px]">account_balance_wallet</span>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">MEL del comercio</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="MEL líquido" value={formatMoney(String(mel.liquidMel))} />
              <MetricCard label="Saldo vigente" value={formatMoney(String(mel.outstanding))} variant="negative" />
              <MetricCard label="MEL disponible" value={formatMoney(String(mel.available))} variant={excedeMel ? 'negative' : 'positive'} />
              <MetricCard label="Solicitado" value={formatMoney(request.totalAmount)} variant={excedeMel ? 'negative' : 'default'} />
            </div>
            {excedeMel && (
              <div className="mt-3 flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
                <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">error</span>
                <div>
                  <p className="font-medium">Esta solicitud excede el MEL disponible.</p>
                  <p className="text-xs mt-0.5">
                    Faltan <strong>{formatMoney(String(Math.abs(melDelta)))}</strong> para que entre en cupo. El backend rechazará la aprobación con código <code className="text-[10px] font-mono bg-red-100 dark:bg-red-900/40 px-1 rounded">MEL_INSUFFICIENT</code>.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {request.rejectionReason && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <span className="material-symbols-outlined text-red-500 text-[20px] mt-0.5">report</span>
            <div>
              <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-1">Motivo de cancelación / rechazo</p>
              <p className="text-sm text-red-800 dark:text-red-300">{request.rejectionReason}</p>
            </div>
          </div>
        )}

        {request.status === 'approved' && request.cancelableUntil && new Date(request.cancelableUntil) > new Date() && (
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <span className="material-symbols-outlined text-amber-500 text-[20px] mt-0.5">hourglass_top</span>
            <div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-1">Ventana de cancelación</p>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Puede cancelarse hasta <strong>{new Date(request.cancelableUntil).toLocaleString('es-CL')}</strong>. Después del desembolso la solicitud queda en cobro y no puede revertirse sin contra-asiento.
              </p>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Monto Total" value={formatMoney(request.totalAmount)} />
          <MetricCard label="Descuento" value={formatMoney(request.discountAmount)} variant="negative" />
          <MetricCard label="TYE" value={formatMoney(request.stampTaxAmount)} variant="negative" />
          <MetricCard label="Monto Neto" value={formatMoney(request.netAmount)} variant="positive" />
        </div>

        {/* Details */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
            <div><p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Tasa Mensual</p><p className="text-slate-800 dark:text-slate-200">{request.monthlyRate ? `${(Number(request.monthlyRate) * 100).toFixed(2)}%` : '—'}</p></div>
            <div><p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Cuotas</p><p className="text-slate-800 dark:text-slate-200">{request.installments.length}</p></div>
            <div><p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Creada</p><p className="text-slate-800 dark:text-slate-200">{new Date(request.createdAt).toLocaleString('es-CL')}</p></div>
            {request.resolutionAt && <div><p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Resuelta</p><p className="text-slate-800 dark:text-slate-200">{new Date(request.resolutionAt).toLocaleString('es-CL')}</p></div>}
          </div>
        </div>

        {/* Installments table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <span className="material-symbols-outlined text-[18px] text-slate-500">receipt_long</span>
            </div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Cuotas incluidas ({request.installments.length})</h3>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">ID Cuota</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 text-right">Monto</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Vencimiento</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 text-right">Días</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 text-right">Comisión</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Cobranza</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Mecanismo</th>
                <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Payment request</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {request.installments.map((inst) => {
                const cs = INSTALLMENT_COLLECTION_STATUS[inst.collectionStatus] ?? { label: inst.collectionStatus, color: 'text-slate-500 bg-slate-100 dark:bg-slate-800' };
                const lpr = inst.lupsPaymentRequest;
                const lprStatus = lpr ? (LUPS_SEND_STATUS[lpr.sendStatus] ?? { label: lpr.sendStatus, color: 'text-slate-500 bg-slate-100 dark:bg-slate-800' }) : null;
                return (
                  <tr key={inst.installmentDetailId} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors align-top">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{inst.installmentDetailId.slice(0, 10)}...</td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-slate-800 dark:text-slate-200">{formatMoney(inst.paymentAmount)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{inst.dueAt || '—'}</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-600 dark:text-slate-400">{inst.advanceDays}d</td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-red-600 dark:text-red-400">{formatMoney(inst.commissionAmount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cs.color}`}>{cs.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {inst.mechanism ? (
                        <span className="text-[11px] font-medium text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-2 py-0.5 rounded-full">{MECHANISM_LABEL[inst.mechanism] ?? inst.mechanism}</span>
                      ) : (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {lpr && lprStatus ? (
                        <div className="space-y-0.5">
                          <span className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${lprStatus.color}`}>{lprStatus.label}</span>
                          {lpr.lupsRequestId && <div className="font-mono text-[10px] text-slate-500">{lpr.lupsRequestId}</div>}
                          {lpr.sendAttempts > 0 && <div className="text-[10px] text-slate-400">{lpr.sendAttempts} intento{lpr.sendAttempts === 1 ? '' : 's'}</div>}
                          {lpr.disableReason && <div className="text-[10px] text-slate-500">{DISABLE_REASON_LABEL[lpr.disableReason] ?? lpr.disableReason}</div>}
                          {lpr.collectedAt && <div className="text-[10px] text-emerald-600">Cobrada {new Date(lpr.collectedAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}</div>}
                          {lpr.abandonedAt && <div className="text-[10px] text-foreground/70">Abandonada {new Date(lpr.abandonedAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}</div>}
                          {lpr.lastError && <div className="text-[10px] text-red-500 truncate max-w-[200px]" title={lpr.lastError}>Error: {lpr.lastError}</div>}
                        </div>
                      ) : (
                        <span className="text-slate-400">Sin crear</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Approve dialog */}
        <Dialog open={approveDialogOpen} onOpenChange={(o) => { setApproveDialogOpen(o); if (!o) setApproveChallenge(''); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[20px]">check_circle</span>
                Aprobar Solicitud
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Vas a aprobar la solicitud por <span className="font-semibold text-slate-900 dark:text-white">{formatMoney(request.netAmount)}</span> de <span className="font-semibold text-slate-900 dark:text-white">{request.companyBusinessName || '—'}</span>. Esta acción no se puede deshacer.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Para confirmar, escribe el nombre exacto de la empresa: <span className="font-bold text-emerald-700 dark:text-emerald-400">{request.companyBusinessName || '—'}</span>
                </label>
                <Input
                  placeholder={request.companyBusinessName || ''}
                  value={approveChallenge}
                  onChange={(e) => setApproveChallenge(e.target.value)}
                  autoFocus
                  className="h-10"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancelar</Button>
              <Button
                disabled={!approveMatches || actionLoading === 'approve' || excedeMel}
                onClick={handleApprove}
                title={excedeMel ? 'No se puede aprobar: el monto solicitado excede el MEL disponible' : undefined}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {actionLoading === 'approve' ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span> Aprobando...</> : 'Confirmar Aprobación'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={(o) => { setRejectDialogOpen(o); if (!o) { setRejectionReason(''); setRejectChallenge(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="material-symbols-outlined text-red-500 text-[20px]">warning</span>
                Rechazar Solicitud
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Vas a rechazar la solicitud de <span className="font-semibold text-slate-900 dark:text-white">{request.companyBusinessName || '—'}</span>. Esta acción no se puede deshacer.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Motivo del rechazo</label>
                <Input
                  placeholder="Describe el motivo del rechazo..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  autoFocus
                  className="h-10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Para confirmar, escribe el nombre exacto de la empresa: <span className="font-bold text-red-600 dark:text-red-400">{request.companyBusinessName || '—'}</span>
                </label>
                <Input
                  placeholder={request.companyBusinessName || ''}
                  value={rejectChallenge}
                  onChange={(e) => setRejectChallenge(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancelar</Button>
              <Button variant="destructive" disabled={!rejectionReason.trim() || !rejectMatches || actionLoading === 'reject'} onClick={handleReject} className="gap-1.5">
                {actionLoading === 'reject' ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span> Rechazando...</> : 'Confirmar Rechazo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
